import React from 'react'
import { motion } from 'motion/react'
import { Check } from 'lucide-react'

/**
 * The selection checkbox shown on each card/row while a collection list surface
 * is in "select to merge" mode. Shared so the People / Places / generic
 * surfaces render an identical control. Uses the CLAUDE.md micro-interaction
 * spring (400/20) with whileHover/whileTap scale; the check mark pops in with
 * the chip enter scale (0.6→1) under the same spring family.
 *
 * The button stops event propagation itself so a card/row can wrap it with its
 * own click handler (open peek) without the checkbox toggling both.
 */

interface MergeCheckboxProps {
    checked: boolean
    onToggle: () => void
    /** Extra positioning classes (e.g. absolute placement on a card). */
    className?: string
}

const MergeCheckbox: React.FC<MergeCheckboxProps> = ({ checked, onToggle, className = '' }) => (
    <motion.button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={checked ? 'Deselect for merge' : 'Select for merge'}
        onClick={(e) => {
            e.stopPropagation()
            onToggle()
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className={`flex items-center justify-center w-6 h-6 rounded-md border-2 shadow-sm ${
            checked
                ? 'bg-amber-100 border-amber-600 text-plum'
                : 'bg-white/90 border-gray-300 text-transparent hover:border-amber-400'
        } ${className}`}
    >
        <motion.span
            initial={false}
            animate={{ scale: checked ? 1 : 0.6, opacity: checked ? 1 : 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
            <Check className="w-4 h-4" strokeWidth={3} />
        </motion.span>
    </motion.button>
)

export default MergeCheckbox
